/**
 * ConversationStage — the thread layout that hands an empty conversation over to
 * a running one without a jump.
 *
 * The composer is ONE element for the whole life of the conversation. While the
 * conversation is empty equal spacers above the hero and under the composer grow,
 * so the hero (the twins and the headline) and the composer sit as one block in
 * the middle of the content area; when the first message
 * lands the spacer collapses and framer-motion's layout animation glides the same
 * composer down to the bottom. Meanwhile the hero steps back (fades up and
 * shrinks a touch) and the thread rises in from just above the composer.
 *
 * Reduced motion keeps the fades and drops every movement (MotionConfig "user").
 */

import { ReactNode, useRef } from 'react'
import { AnimatePresence, MotionConfig, motion } from 'framer-motion'

import { cn } from '@/lib/utils'

/** The compose modal's curve, so the hand-over moves like the rest of the app. */
const EASE_OUT: [number, number, number, number] = [0.32, 0.72, 0, 1]

interface ConversationStageProps {
  isEmpty: boolean
  /** Shown above the centered composer while the conversation is empty. */
  emptyHero: ReactNode
  /** The message scroller, mounted once the conversation has started. */
  thread: ReactNode
  /** Status lines that sit right above the composer (send error, remaining count). */
  beforeComposer?: ReactNode
  composer: ReactNode
}

export function ConversationStage(props: ConversationStageProps) {
  const { isEmpty, emptyHero, thread, beforeComposer, composer } = props

  // Only a conversation that was empty on this screen gets the thread entrance;
  // opening an existing conversation shows its messages straight away.
  const hasBeenEmptyRef = useRef(isEmpty)
  if (isEmpty) {
    hasBeenEmptyRef.current = true
  }

  return (
    <MotionConfig reducedMotion="user">
      {/* Grows while empty, matching the spacer under the composer, so the hero and
          the composer sit as one block in the middle of the content area. */}
      <div aria-hidden="true" className={cn(isEmpty ? 'flex-1' : 'flex-none')} />

      {/* While empty the region keeps the hero's full height (no min-h-0), so a short
          window pushes the composer down instead of the hero spilling over the header. */}
      <div className={cn('relative flex flex-col', isEmpty ? 'flex-none' : 'min-h-0 flex-1')}>
        {!isEmpty && (
          <motion.div
            className="flex min-h-0 flex-1 flex-col"
            initial={hasBeenEmptyRef.current ? { opacity: 0, y: 16 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15, ease: EASE_OUT }}
            data-testid="conversation-stage-thread"
          >
            {thread}
          </motion.div>
        )}

        {/* popLayout lifts the leaving hero out of the flow, so the thread and the
            composer move at once instead of waiting for the fade to finish. */}
        <AnimatePresence initial={false} mode="popLayout">
          {isEmpty && (
            <motion.div
              key="empty-hero"
              exit={{ opacity: 0, y: -12, scale: 0.97 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              data-testid="conversation-stage-hero"
            >
              {emptyHero}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {beforeComposer}

      <motion.div layout="position" transition={{ duration: 0.42, ease: EASE_OUT }} className="shrink-0">
        {composer}
      </motion.div>

      {/* Grows while empty so the block rests mid-page; collapses to hand the composer down. */}
      <div aria-hidden="true" className={cn(isEmpty ? 'flex-1' : 'flex-none')} />
    </MotionConfig>
  )
}
